import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Divider,
  LinearProgress,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as LeftArrowIcon } from "../Assets/LeftArrow.svg";
import { ReactComponent as CloseIcon } from "../Assets/close.svg";
import axios from "axios";
import { useGlobalAuthContext } from "../context/AuthContext";
import DragAndDropFile from "./Account/drag-and-drop";
import { ReactComponent as UploadCompleteIcon } from "../Assets/upload-complete.svg";
import { useDispatch } from "react-redux";
import Cookies from "js-cookie";
import { login } from "../Slices/roomNateSlice";

import { baseUrl } from "../utils/baseUrl";

const NewUpload = ({
  setValue,
  onCancel,
  uploadImage,
  payloadData,
  progress,
  setProgress,
  setActiveLayout,
  click,
  setVersionLayout,
  setProjectDetails,
  projectDetails,
  setUploadImage,
  errorProcessImageRef,
  disclaimer
}) => {

  // Removed cleanup effect that was clearing uploadImage when progress was 0
  // This was causing the progress bar not to show on first upload 

  useEffect(() => {
    setProjectDetails((project) => ({
      ...project,
      name: "Untitled",
    }));
  }, [setProjectDetails]);

  const navigate = useNavigate();
  const Dispatch = useDispatch()
  const [receievedToken, setReceievedToken] = useState("")
  const { currentUserSessionStorage  , dispatch , partnerName} = useGlobalAuthContext();

  const [loadingUpload, setLoadingUpload] = useState(false);

  const initiateFileUpload = React.useCallback(async () => {
    try {
      const formData = new FormData();
      formData.append("file", uploadImage);
      

      // Use canonical baseUrl, version hardcoded, no env vars
      const uploadUrl = `${baseUrl}/g/fileUpload?generateSizes=true`;
      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            Authorization: currentUserSessionStorage,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response;
    } catch (error) {
      throw error;
    }
  }, [uploadImage, currentUserSessionStorage]);

  useEffect(() => {
    if (!uploadImage) return;

    const handleFileUpload = async () => {
      try {
        const imageUploadResponse = await initiateFileUpload();
        console.log('Image upload response:', imageUploadResponse?.data);
        
        setProjectDetails((project) => ({
          ...project,
          image: imageUploadResponse?.data?.id,
        }));

        if (progress == 99 && imageUploadResponse?.data?.id) {
          setProgress(100);
        } else if (progress == 99 && !imageUploadResponse?.data?.id) {
          console.error('Image upload failed - no ID returned');
          setUploadImage(null);
          errorProcessImageRef.current = true;
        }
      } catch (error) {
        console.error('Image upload error:', error);
        if (progress == 99) {
          setUploadImage(null);
          errorProcessImageRef.current = true;
        }
      }
    };

    handleFileUpload();
  }, [uploadImage, initiateFileUpload, setProjectDetails, progress, setProgress, setUploadImage, errorProcessImageRef]);

  const upload = React.useCallback(async (e , authToken) => {
    console.log('Upload started with projectDetails:', projectDetails);
    
    setLoadingUpload(true);
    setVersionLayout(2);

    try {
      const token = authToken || currentUserSessionStorage;

      const response = await axios.post(
        `${baseUrl}/savedLayout/add`,
        {
          name: projectDetails?.name,
          payload: {
            ...payloadData,
            origin: window?.location.origin,
          },
          image: projectDetails?.image,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('Layout save response:', response.data);

      setActiveLayout({
        name: projectDetails?.name,
        uuid: response.data.uniqueId,
      });

      navigate(`/floor-plan/${response.data.uniqueId}/version/${2}`);

      setValue("1");
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      if(authToken){
        dispatch({ type: "LOGIN", payload: authToken });
        Cookies.set("token", JSON.stringify(authToken), {
          expires: 30,
        });
        Dispatch(login(authToken));
      }
      setLoadingUpload(false);
    }
  }, [currentUserSessionStorage, projectDetails, payloadData, setActiveLayout, navigate, setValue, dispatch, Dispatch, setVersionLayout]);

  const loginFunc = async () => {
    window.open('/signup', '_blank');
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
  
      const { authToken } = event.data;
  
      if (authToken) {
        setReceievedToken(authToken)

      }
    };
  
    window.addEventListener("message", handleMessage);
  
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(()=>{
    if(receievedToken){
        upload(null, receievedToken);
    }
  },[receievedToken, upload])

  // Auto-upload when image is ready and user is already logged in
  useEffect(() => {
    if (progress === 100 && currentUserSessionStorage && projectDetails?.image && !loadingUpload) {
      console.log('Auto-uploading for logged-in user with ready image');
      // Commenting out auto-upload to keep progress view on /main page
      // upload();
    }
  }, [progress, currentUserSessionStorage, projectDetails?.image, loadingUpload, upload]);

  return (
    <>
      {!uploadImage && <DragAndDropFile uploadImageProp={true} click={click} />}

      {uploadImage &&  (
        <>
          {progress < 100 ? (
            <>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    padding: "16px",
                    border: "1px solid rgba(231, 231, 231, 1)",
                    borderRadius: "12px",
                  }}
                >
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: "600",
                      fontSize: "12px",
                      color: "rgba(11, 11, 11, 1)",
                    }}
                  >
                    Uploading...
                  </Typography>
                  <Typography
                    color="textSecondary"
                    sx={{
                      color: "rgba(109, 109, 109, 1)",
                      fontSize: "12px",
                    }}
                  >
                    {progress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      width: "100%",
                      mt: 1,
                      height: "8px",
                      borderRadius: "40px",
                      backgroundColor: "rgba(250, 250, 250, 1)",
                      "& .MuiLinearProgress-bar": {
                        background: (theme) => theme.gradients.primary,
                      },
                    }}
                  />
                </Box>

                <Button
                  variant="text"
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                    color: "rgba(255, 81, 81, 1)",
                    fontSize: "13px",
                  }}
                  startIcon={<CloseIcon />}
                  onClick={onCancel}
                >
                  Cancel Upload
                </Button>
              </Box>
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
            >
              <UploadCompleteIcon />

              <Typography
                mt={1}
                mb={3}
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "rgba(11, 11, 11, 1)",
                }}
              >
                Upload Complete
              </Typography>
            </Box>
          )}
        </>
      )}

      {disclaimer && !uploadImage &&( 
      <>
              <Box mt={2} mb={1} textAlign={"center"}>
                <Typography fontSize="10px" component="div">
                  By uploading your floor plan, you agree to the MagicFurnish {" "}
                  <Link
                    href="https://www.magicfurnish.com/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    color={"#202124"}
>
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="https://www.magicfurnish.com/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    color={"#202124"}

                  >
                    Privacy Policy
                  </Link>
                </Typography>
              </Box>

              <Divider/>

              <Box mt={1}>
                <Typography fontSize="8px" component="div">
                Please note that floor plans and layouts generated by MagicFurnish are based on third-party information that may be incomplete, inaccurate, or outdated. {partnerName ||"MagicFurnish"} relies on that information as provided and does not guarantee its accuracy. Any dimensions, layouts, or furniture placements should be independently verified before being used for construction, permitting, or other decision-making. Customers are solely responsible for compliance with all applicable building codes, fire codes, accessibility requirements, and other regulations. Any use of these plans is at the user's own risk, and they should be reviewed by appropriate licensed professionals before any work begins.
                </Typography>
              </Box>
      </>
      )
      }


      {uploadImage && <Box>
        <TextField
          label="Name your project"
          variant="filled"
          fullWidth
          FormHelperTextProps={{
            style: {
              color: "rgba(154, 154, 154, 1)",
              fontSize: "14px",
              marginLeft: "12px",
              marginRight: "12px",
            },
          }}
          sx={{
            "& .MuiInputBase-root": {
              bgcolor: "rgba(230, 240, 240, 0.4) !important",
              borderRadius: "12px",
              borderBottom: "0",
            },
            "& .MuiInputBase-root:before": {
              border: "none !important",
            },
            "& .MuiInputBase-root:hover:after": {
              border: "none !important",
            },
            "& .MuiInputBase-root:after": {
              border: "none !important",
            },
            "& .MuiInputBase-root:hover:before": {
              border: "none !important",
            },
            "& .MuiFormLabel-root": {
              color: "rgba(154, 154, 154, 1) !important",
              fontSize: "14px",
            },
          }}
          value={projectDetails?.name}
          helperText="You can change this later"
          onChange={(e) =>
            setProjectDetails({ ...projectDetails, name: e.target.value })
          }
        />

        {progress == 100 && (
          <Box width={"100%"} mt={2}>
            <LoadingButton
              variant="contained"
              className="button-style"
              fullWidth
              endIcon={!currentUserSessionStorage ? <LeftArrowIcon /> : null}
              onClick={currentUserSessionStorage ? (e)=> upload(e) : loginFunc}
              loading={loadingUpload}
            >
              {currentUserSessionStorage? "Get Started" : "Create an account to continue"}
            </LoadingButton>
          </Box>
        )}
      </Box>}
    </>
  );
};
export default NewUpload;
